import { useMemo, useState } from "react";
import PageHero from "@/components/feature/PageHero";
import CtaBand from "@/components/feature/CtaBand";
import WorkCard from "@/components/feature/WorkCard";
import { works, workTypes } from "@/mocks/works";

const PAGE_SIZE = 6;

export default function Works() {
  const years = useMemo(
    () => ["全部", ...Array.from(new Set(works.map((w) => w.year))).sort((a, b) => b - a).map(String)],
    []
  );

  const [type, setType] = useState("全部");
  const [year, setYear] = useState("全部");
  const [sort, setSort] = useState<"latest" | "popular">("latest");
  const [page, setPage] = useState(1);

  const filtered = useMemo(() => {
    const list = works.filter(
      (w) => (type === "全部" || w.type === type) && (year === "全部" || String(w.year) === year)
    );
    return list.sort((a, b) =>
      sort === "latest" ? b.year - a.year : b.popularity - a.popularity
    );
  }, [type, year, sort]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pageItems = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const resetFilters = () => {
    setType("全部");
    setYear("全部");
    setSort("latest");
    setPage(1);
  };

  const update = (fn: () => void) => {
    fn();
    setPage(1);
  };

  return (
    <div>
      <PageHero
        title="作品案例"
        subtitle="涵盖影视项目、广告案例与节目作品，每一个案例都是芒果传媒对内容品质的坚持。"
        image="https://readdy.ai/api/search-image?query=Cinematic%20film%20reel%20and%20clapperboard%20on%20a%20dark%20stage%20with%20warm%20amber%20spotlight%2C%20artistic%20media%20atmosphere%2C%20no%20text&width=1920&height=700&seq=mango-works-hero&orientation=landscape"
        crumbs={[{ label: "作品案例" }]}
      />

      <section className="bg-background-50 py-10 md:py-14">
        <div className="container-site">
          <div className="bg-secondary-50 rounded-lg p-5 md:p-6 border border-background-200/80">
            <div className="flex flex-col lg:flex-row lg:items-center gap-5">
              <div className="flex-1">
                <span className="text-xs font-semibold text-foreground-500 block mb-2">作品类型</span>
                <div className="flex flex-wrap gap-2">
                  {workTypes.map((t) => (
                    <button
                      key={t}
                      type="button"
                      onClick={() => update(() => setType(t))}
                      className={`px-3.5 py-2 rounded-full text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                        type === t
                          ? "bg-primary-500 text-foreground-950"
                          : "bg-background-50 text-foreground-700 hover:bg-primary-50"
                      }`}
                    >
                      {t}
                    </button>
                  ))}
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-5">
                <div>
                  <span className="text-xs font-semibold text-foreground-500 block mb-2">拍摄年份</span>
                  <select
                    value={year}
                    onChange={(e) => update(() => setYear(e.target.value))}
                    className="w-full sm:w-32 px-3 py-2 rounded-md bg-background-50 border border-background-200 text-sm text-foreground-800 focus:outline-none focus:ring-2 focus:ring-primary-300"
                  >
                    {years.map((y) => (
                      <option key={y} value={y}>
                        {y === "全部" ? "全部年份" : `${y} 年`}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <span className="text-xs font-semibold text-foreground-500 block mb-2">排序方式</span>
                  <select
                    value={sort}
                    onChange={(e) => update(() => setSort(e.target.value as "latest" | "popular"))}
                    className="w-full sm:w-32 px-3 py-2 rounded-md bg-background-50 border border-background-200 text-sm text-foreground-800 focus:outline-none focus:ring-2 focus:ring-primary-300"
                  >
                    <option value="latest">最新发布</option>
                    <option value="popular">最受欢迎</option>
                  </select>
                </div>
              </div>
            </div>
          </div>

          <div className="flex items-center justify-between mt-6">
            <p className="text-sm text-foreground-600">
              共找到 <span className="font-semibold text-foreground-900">{filtered.length}</span> 个作品
            </p>
            <p className="text-sm text-foreground-600 hidden sm:block">
              第 {currentPage} / {totalPages} 页
            </p>
          </div>

          {pageItems.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mt-6">
              {pageItems.map((w) => (
                <WorkCard key={w.id} work={w} />
              ))}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-20 text-center">
              <span className="w-16 h-16 rounded-full bg-secondary-100 flex items-center justify-center">
                <i className="ri-search-eye-line text-3xl text-secondary-600"></i>
              </span>
              <h3 className="font-heading font-bold text-lg text-foreground-950 mt-5">
                暂无符合条件的作品
              </h3>
              <p className="text-sm text-foreground-600 mt-2">
                换一个筛选条件试试，或重置筛选查看全部作品。
              </p>
              <button
                type="button"
                onClick={resetFilters}
                className="mt-6 px-5 py-2.5 rounded-md bg-primary-500 text-foreground-950 font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                重置筛选
              </button>
            </div>
          )}

          {totalPages > 1 && pageItems.length > 0 && (
            <div className="flex items-center justify-center gap-2 mt-12">
              <button
                type="button"
                disabled={currentPage === 1}
                onClick={() => setPage(currentPage - 1)}
                className="px-4 py-2 rounded-md border border-background-200 text-sm font-medium text-foreground-700 hover:bg-secondary-50 disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer"
              >
                上一页
              </button>
              {Array.from({ length: totalPages }).map((_, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => setPage(i + 1)}
                  className={`w-10 h-10 rounded-md text-sm font-medium transition-colors cursor-pointer ${
                    currentPage === i + 1
                      ? "bg-primary-500 text-foreground-950"
                      : "border border-background-200 text-foreground-700 hover:bg-secondary-50"
                  }`}
                >
                  {i + 1}
                </button>
              ))}
              <button
                type="button"
                disabled={currentPage === totalPages}
                onClick={() => setPage(currentPage + 1)}
                className="px-4 py-2 rounded-md border border-background-200 text-sm font-medium text-foreground-700 hover:bg-secondary-50 disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer"
              >
                下一页
              </button>
            </div>
          )}
        </div>
      </section>

      <CtaBand />
    </div>
  );
}